import Button from '@components/ui/ButtonV2/Button'
import InputV2 from '@components/ui/InputV2/InputV2'
import { OtpResponse } from '@components/ui/OtpInput/OtpInput'
import { customerPasswordReset } from '@features/account/api/customer-password-reset'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCustomErrors } from '@lib/hooks/useCustomErrors'
import { closeModal } from '@redux/modalSlice'
import clsx from 'clsx'
import { signIn } from 'next-auth/react'
import { useRouter } from 'next/router'
import { FC, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { ImSpinner2 } from 'react-icons/im'
import { useDispatch } from 'react-redux'
import { z } from 'zod'
import s from './M58ForgotPasswordForm.module.scss'

type Step2FormData = {
  email: string
  tokenString: string
  password: string
}

type SetPasswordStepProps = {
  email: string
  token: string
  className?: string
  disableLoginSwitch?: boolean
}

const step2Schema = z.object({
  tokenString: z.string().min(6, 'Required'),
  password: z
    .string()
    .min(1, 'Required')
    .regex(/[A-Z]/, '')
    .regex(/[a-z]/, '')
    .regex(/\d/, '')
    .regex(/[\W_]/, ''),
  confirmPassword: z
    .string()
    .min(1, 'Required')
    .regex(/[A-Z]/, '')
    .regex(/[a-z]/, '')
    .regex(/\d/, '')
    .regex(/[\W_]/, ''),
})

const SetPasswordStep: FC<SetPasswordStepProps> = ({
  className,
  email,
  token,
  disableLoginSwitch,
}) => {
  const step2Methods = useForm({
    resolver: zodResolver(step2Schema),
    defaultValues: {
      email: email,
      tokenString: token,
      password: '',
      confirmPassword: '',
    },
  })

  const {
    formState: { errors: step2Errors },
    watch,
  } = step2Methods

  const { addError, loading, customErrors, clearErrors } = useCustomErrors()
  const [loadingSpinner, setLoadingSpinner] = useState(false)

  const dispatch = useDispatch()
  const router = useRouter()

  const resetPasswordAction: SubmitHandler<Step2FormData> = async (data) => {
    setLoadingSpinner(true)

    const response = await customerPasswordReset({
      email,
      resetToken: token,
      password: data.password,
    })
    const hasErrors = false

    if (!hasErrors) {
      clearErrors()
      if (!disableLoginSwitch) {
        const response = await signIn('credentials', {
          email: email,
          password: step2Methods.getValues('password'),
          redirect: false,
        })

        if (response.ok) {
          return router.push(
            `/${router.query.language}/${router.query.country}/account/dashboard`,
          )
        } else {
          setLoadingSpinner(false)

          addError({
            code: 'unknown',
            message: 'Failed to sign in',
          })
        }
      } else {
        dispatch(closeModal())

        // remove query params, so the modal will not open again
        router.replace(router.asPath.split('?')[0])
      }
    } else {
      setLoadingSpinner(false)

      // for (const error of response.customerPasswordReset) {
      //   // addError({
      //   //   code: error.code,
      //   //   message: error.message,
      //   // })
      // }
    }
  }

  // Function to check each password requirement
  const passwordValue = watch('password', '')

  const checkPasswordRequirements = (password: string) => {
    return {
      minLength: password.length >= 6,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      digit: /\d/.test(password),
      nonAlphanumeric: /[\W_]/.test(password),
      matching:
        password === step2Methods.getValues('confirmPassword') &&
        password.length > 0,
    }
  }

  const passwordRequirements = checkPasswordRequirements(passwordValue)

  return (
    <FormProvider {...step2Methods}>
      <form
        className={clsx(s.form, className)}
        onSubmit={step2Methods.handleSubmit(resetPasswordAction)}
      >
        {customErrors && customErrors.length > 0 && (
          <div className={s.errorContainer}>
            {customErrors.map((error) => (
              <div key={error.code} className={s.error}>
                {error.message}
              </div>
            ))}
          </div>
        )}

        <input
          type="hidden"
          name="email"
          value={email}
          {...step2Methods.register('email')}
        />
        <input
          type="hidden"
          name="tokenString"
          value={token}
          {...step2Methods.register('tokenString')}
        />
        <InputV2
          id="password"
          type="password"
          placeholder="Password"
          required
          errorMessage={step2Errors.password?.message as string}
        />
        <InputV2
          id="confirmPassword"
          type="password"
          placeholder="Confirm Password"
          required
          errorMessage={step2Errors.confirmPassword?.message as string}
        />
        <div className={s.passwordRequirements}>
          <ul>
            <li
              className={clsx(
                { [s.valid]: passwordRequirements.minLength },
                {
                  [s.error]:
                    !passwordRequirements.minLength &&
                    step2Methods.formState.touchedFields.password,
                },
              )}
            >
              At least 6 characters long
            </li>
            <li
              className={clsx(
                {
                  [s.valid]:
                    passwordRequirements.uppercase &&
                    passwordRequirements.lowercase &&
                    passwordRequirements.digit,
                },
                {
                  [s.error]:
                    (!passwordRequirements.uppercase ||
                      !passwordRequirements.lowercase ||
                      !passwordRequirements.digit) &&
                    step2Methods.formState.touchedFields.password,
                },
              )}
            >
              Contains uppercase, lowercase, and one number
            </li>
            <li
              className={clsx(
                { [s.valid]: passwordRequirements.nonAlphanumeric },
                {
                  [s.error]:
                    !passwordRequirements.nonAlphanumeric &&
                    step2Methods.formState.touchedFields.password,
                },
              )}
            >
              Contains a special character
            </li>
            <li
              className={clsx(
                { [s.valid]: passwordRequirements.matching },
                {
                  [s.error]:
                    !passwordRequirements.matching &&
                    step2Methods.formState.touchedFields.confirmPassword &&
                    step2Methods.formState.touchedFields.password,
                },
              )}
            >
              Passwords match
            </li>
          </ul>
        </div>

        <div className={s.submitWrapper}>
          <Button
            className={s.button}
            variant="secondary"
            type="submit"
            disabled={
              !step2Methods.formState.isValid || loading || loadingSpinner
            }
          >
            {loading || loadingSpinner ? (
              <ImSpinner2 className={s['icon-spin']} />
            ) : (
              'Submit'
            )}
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}

export const actionOtpSendToken: (
  email: string,
) => Promise<OtpResponse> = async (email: string) => {
  const response = await fetch('/api/account/send-reset-token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email: email,
    }),
  })

  if (!response.ok) {
    throw new Error('Failed to send OTP')
  }

  return response.json()
}

export default SetPasswordStep
